import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import qs from 'query-string';
import { isEqual } from 'lodash';

import { QueryStringParams } from '@xtnd/web/shared/types';

/**
 * React hook for synchronizing table parameters (pagination, filters, sorting)
 * with the browser URL query string.
 *
 * This hook:
 * - Parses query string params from the URL.
 * - Maintains internal state (`tableParams`) representing current table settings.
 * - Updates the URL automatically when params change.
 * - Allows programmatic updates via `setParams`.
 * - Provides a `resetParams` helper to restore defaults.
 *
 * @param {Partial<QueryStringParams>} [initial] - Optional default values for table params.
 *
 * @returns {{
*   tableParams: QueryStringParams;
*   setParams: (update: Partial<QueryStringParams>) => void;
*   resetParams: () => void;
* }}
* - **tableParams**: Current merged params from URL and state.
* - **setParams**: Function to update pagination, filters, or sorts and sync URL.
* - **resetParams**: Resets table params back to initial defaults.
*
* @example
* ```tsx
* const { tableParams, setParams, resetParams } = useTableParams({
*   page: 1,
*   limit: 25,
*   filters: { status: 'APPEAL_SUBMITTED' }
* });
*
* Update pagination
* setParams({ page: 2 });
*
* Add filter
* setParams({
*   filters: { status: 'CLOSED' }
* });
*
* Reset all params
* resetParams();
* ```
*
* @remarks
* - Filters & sorts are JSON encoded in the URL.
* - URL sync uses `replace: true` to avoid polluting browser history.
* - Prevents infinite loops by comparing old/new params before updating.
* - Only non-empty filters & sorts are preserved in URL.
*/
export const useTableParams = (initial?: Partial<QueryStringParams>) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  /**
   * parseParams()
   * --------------------------------------------------------------------
   * - Reads query parameters from the current URL
   * - Converts page/limit to numbers
   * - Parses JSON-encoded filters and sorts
   * - Falls back to initial defaults when URL does not provide values
   *
   * This function normalizes all table parameters into a
   * consistent QueryStringParams object.
   */
  const parseParams = useCallback((): QueryStringParams => {
    const params = qs.parse(searchParams.toString());

    return {
      page: Number(params.page) || initial?.page || 1,
      limit: Number(params.limit) || initial?.limit || 25,
      filters: params.filters ? JSON.parse(params.filters as string) : initial?.filters || {},
      sorts: params.sorts ? JSON.parse(params.sorts as string) : initial?.sorts || {},
    };
  }, [searchParams, initial]);

  /**
   * tableParams state
   * --------------------------------------------------------------------
   * - Stores the current table parameters
   * - Initialized from parseParams()
   */
  const [tableParams, setTableParams] = useState<QueryStringParams>(parseParams);

  /**
   * Sync URL → State
   * --------------------------------------------------------------------
   * - Whenever the URL changes (back/forward navigation, page reload, etc.)
   *   parseParams() extracts the new parameters.
   * - We compare them with the existing tableParams state.
   * - If they differ, we update the state.
   *
   * This prevents infinite loops between state updates and URL updates.
   */
  useEffect(() => {
    const newParams = parseParams();

    const hasChanged =
      tableParams.page !== newParams.page ||
      tableParams.limit !== newParams.limit ||
      !isEqual(tableParams.filters, newParams.filters) ||
      !isEqual(tableParams.sorts, newParams.sorts);

    if (hasChanged) {
      setTableParams(newParams);
    }
  }, [searchParams, parseParams, tableParams]);

  /**
   * setParams()
   * --------------------------------------------------------------------
   * - Used by the UI to update table parameters:
   *   pagination, filters, sorting, etc.
   * - Merges updates into the existing tableParams state.
   * - Serializes filters/sorts into JSON for URL persistence.
   * - Removes empty filters/sorts to keep the URL clean.
   * - Updates the browser URL using navigate()
   *   but only if the query string actually changed.
   * - Finally updates state to keep everything in sync.
   *
   * This function ensures state and URL are always aligned.
   */
  const setParams = useCallback(
    (update: Partial<QueryStringParams>) => {
      const newParams = { ...tableParams, ...update };

      // Read current URL parameters
      const currentParams = qs.parse(searchParams.toString());

      const updatedQuery: Record<string, any> = {
        ...currentParams,
        page: newParams.page,
        limit: newParams.limit,
      };

      // Handle filters
      if (newParams.filters && Object.keys(newParams.filters).length > 0) {
        updatedQuery.filters = JSON.stringify(newParams.filters);
      } else {
        delete updatedQuery.filters;
      }

      // Handle sorts
      if (newParams.sorts && Object.keys(newParams.sorts).length > 0) {
        updatedQuery.sorts = JSON.stringify(newParams.sorts);
      } else {
        delete updatedQuery.sorts;
      }

      const query = qs.stringify(updatedQuery);

      // Only navigate if URL is actually different
      if (query !== searchParams.toString()) {
        navigate(`?${query}`, { replace: true });
      }

      // Update state
      setTableParams(newParams);
    },
    [navigate, tableParams, searchParams]
  );

  /**
   * resetParams()
   * --------------------------------------------------------------------
   * - Restores table parameters back to initial defaults.
   * - Reuses setParams() so URL and state remain synchronized.
   */
  const resetParams = useCallback(() => {
    setParams(initial || { page: 1, limit: 25, filters: {}, sorts: {} });
  }, [setParams, initial]);

  return { tableParams, setParams, resetParams };
};

